import { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import {
  Button,
  EmptyState,
  FormField,
  LoadingState,
  Modal,
  SectionCard,
  StatusBadge,
  TextAreaInput,
} from "../../components/ui";
import { pontoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { Tabela, Td, Th } from "./TabelaCompacta";
import { dataCompleta } from "./pontoUtils";

const SITUACOES = {
  PENDENTE: { rotulo: "Pendente", variante: "warning" },
  APROVADO: { rotulo: "Aprovado", variante: "success" },
  NEGADO: { rotulo: "Negado", variante: "danger" },
};

const formatarDataHora = (valor) => {
  if (!valor) return "";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? valor
    : data.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

/**
 * Pedidos de ajuste enviados pelas colaboradoras. Aprovar aplica o ajuste no cartão
 * (com o motivo como justificativa); negar só registra a resposta.
 */
const SolicitacoesTab = ({ onAlterado }) => {
  const { showError, showSuccess } = useNotification();
  const [filtro, setFiltro] = useState("PENDENTE");
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [resposta, setResposta] = useState({
    acao: null,
    solicitacao: null,
    texto: "",
  });
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setSolicitacoes(await pontoService.listarSolicitacoes(filtro));
    } catch (erro) {
      showError(erro.message);
    } finally {
      setCarregando(false);
    }
  }, [filtro]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregar();
  }, [carregar]);

  const fechar = () =>
    setResposta({ acao: null, solicitacao: null, texto: "" });

  const confirmar = async () => {
    setSalvando(true);
    try {
      if (resposta.acao === "aprovar") {
        await pontoService.aprovarSolicitacao(
          resposta.solicitacao.id,
          resposta.texto.trim(),
        );
        showSuccess("Solicitação aprovada e cartão ajustado.");
      } else {
        await pontoService.negarSolicitacao(
          resposta.solicitacao.id,
          resposta.texto.trim(),
        );
        showSuccess("Solicitação negada.");
      }
      fechar();
      await carregar();
      onAlterado();
    } catch (erro) {
      showError(erro.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard bodyClassName="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1">
          {[
            ["PENDENTE", "Pendentes"],
            ["TODAS", "Todas"],
          ].map(([valor, rotulo]) => (
            <button
              key={valor}
              type="button"
              onClick={() => setFiltro(valor)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                filtro === valor
                  ? "bg-blue-600 text-white"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              {rotulo}
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-500">
          O cartão só muda quando você aprova. O horário original fica no
          histórico.
        </p>
      </SectionCard>

      <SectionCard bodyClassName="p-0">
        {carregando ? (
          <LoadingState />
        ) : solicitacoes.length === 0 ? (
          <EmptyState
            title={
              filtro === "PENDENTE"
                ? "Nenhuma solicitação pendente."
                : "Nenhuma solicitação."
            }
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th>Colaboradora</Th>
                <Th>Dia</Th>
                <Th>Pedido</Th>
                <Th>Motivo</Th>
                <Th>Situação</Th>
                <Th className="text-right">Ações</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {solicitacoes.map((solicitacao) => {
                const situacao = SITUACOES[solicitacao.status];
                return (
                  <tr key={solicitacao.id} className="align-top">
                    <Td>
                      <p className="font-medium text-gray-900">
                        {solicitacao.nomeColaboradora}
                      </p>
                      <p className="text-xs text-gray-500">
                        enviado {formatarDataHora(solicitacao.solicitadoEm)}
                      </p>
                    </Td>
                    <Td className="tabular-nums">
                      {dataCompleta(solicitacao.data)}
                      <p className="text-xs text-gray-500">
                        {solicitacao.diaSemana}
                      </p>
                    </Td>
                    <Td className="font-medium">{solicitacao.descricao}</Td>
                    <Td className="max-w-[320px] !whitespace-normal text-gray-600">
                      {solicitacao.motivo}
                    </Td>
                    <Td className="!whitespace-normal">
                      <StatusBadge variant={situacao?.variante}>
                        {situacao?.rotulo || solicitacao.status}
                      </StatusBadge>
                      {solicitacao.respondidoPor && (
                        <p className="mt-1 text-xs text-gray-500">
                          {solicitacao.respondidoPor},{" "}
                          {formatarDataHora(solicitacao.respondidoEm)}
                        </p>
                      )}
                      {solicitacao.resposta && (
                        <p className="mt-1 max-w-[220px] text-xs text-gray-600">
                          {solicitacao.resposta}
                        </p>
                      )}
                    </Td>
                    <Td className="text-right">
                      {solicitacao.status === "PENDENTE" && (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="success"
                            icon={Check}
                            onClick={() =>
                              setResposta({
                                acao: "aprovar",
                                solicitacao,
                                texto: "",
                              })
                            }
                          >
                            Aprovar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={X}
                            onClick={() =>
                              setResposta({
                                acao: "negar",
                                solicitacao,
                                texto: "",
                              })
                            }
                          >
                            Negar
                          </Button>
                        </div>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        )}
      </SectionCard>

      <Modal
        open={Boolean(resposta.acao)}
        onClose={fechar}
        title={resposta.acao === "aprovar" ? "Aprovar ajuste" : "Negar ajuste"}
        description={
          resposta.solicitacao
            ? `${resposta.solicitacao.nomeColaboradora} · ${dataCompleta(resposta.solicitacao.data)}`
            : null
        }
        className="max-w-md"
      >
        {resposta.solicitacao && (
          <div className="space-y-4">
            <div className="rounded-lg bg-gray-50 p-3 text-sm">
              <p className="font-medium text-gray-900">
                {resposta.solicitacao.descricao}
              </p>
              <p className="mt-1 text-gray-600">
                {resposta.solicitacao.motivo}
              </p>
            </div>
            <FormField
              label="Resposta para a colaboradora"
              htmlFor="resposta-solicitacao"
              hint="Opcional."
            >
              <TextAreaInput
                id="resposta-solicitacao"
                maxLength={500}
                value={resposta.texto}
                onChange={(evento) =>
                  setResposta({ ...resposta, texto: evento.target.value })
                }
              />
            </FormField>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={fechar}>
                Cancelar
              </Button>
              <Button
                variant={resposta.acao === "aprovar" ? "success" : "danger"}
                loading={salvando}
                onClick={confirmar}
              >
                {resposta.acao === "aprovar"
                  ? "Aprovar e ajustar o cartão"
                  : "Negar"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SolicitacoesTab;
